import React, { useState, useEffect } from 'react';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

// Tour step definitions
const TOURS = {
  welcome: {
    id: 'welcome',
    name: 'Welcome Tour',
    description: 'Introduction to the DCS ATS interface',
    steps: [
      {
        target: '[data-testid="sidebar"]',
        content: 'Welcome to DCS ATS! This is your sidebar navigation. It changes based on your workspace mode.',
        disableBeacon: true,
        placement: 'right',
      },
      {
        target: '[data-testid="sidebar-my-desk"]',
        content: 'My Desk is your productivity hub. Start every day here to see your action queue and priorities.',
        placement: 'right',
      },
      {
        target: '[data-testid="action-queue"]',
        content: 'The Action Queue shows your prioritized tasks. Work from top to bottom.',
        placement: 'right',
      },
      {
        target: '[data-testid="pipeline-board"]',
        content: 'The Pipeline Board shows all requirements and their candidate stages at a glance.',
        placement: 'top',
      },
      {
        target: '[data-testid="quick-action-btn"]',
        content: 'Press Q anywhere to open Quick Actions. Add candidates, create requirements, and more in one click.',
        placement: 'bottom',
      },
      {
        target: '[data-testid="nav-requirements"]',
        content: 'The sidebar is organised into simple sections: Main, Client Work, Candidates & Jobs, and Business Tools - everything is one click away.',
        placement: 'right',
      },
      {
        target: '[data-testid="daily-targets"]',
        content: 'Your daily targets show CVs uploaded, submissions, and follow-ups. Meet these to stay on track!',
        placement: 'bottom',
      },
      {
        target: 'a[href="/ats/help"]',
        content: 'Need help? Click here anytime to access the Help Center with step-by-step guides.',
        placement: 'right',
      },
    ],
  },
  'add-candidate': {
    id: 'add-candidate',
    name: 'Add Your First Candidate',
    description: 'Learn how to add and parse candidates',
    steps: [
      {
        target: '[data-testid="sidebar-candidates"]',
        content: 'Let\'s add your first candidate. Click on Candidates in the sidebar.',
        disableBeacon: true,
        placement: 'right',
      },
      {
        target: '[data-testid="add-candidate-btn"]',
        content: 'Click "Add Candidate" to open the candidate form.',
        placement: 'bottom',
      },
      {
        target: '[data-testid="cv-upload"]',
        content: 'Upload a CV/resume here. Our AI will automatically extract all the details.',
        placement: 'right',
      },
      {
        target: '[data-testid="candidate-form"]',
        content: 'Review and adjust the auto-filled information. Then click Save.',
        placement: 'left',
      },
    ],
  },
};

const GuidedTours = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [currentTour, setCurrentTour] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [completedTours, setCompletedTours] = useState([]);
  const [showWelcomePrompt, setShowWelcomePrompt] = useState(false);

  // Check tour completion status on mount
  useEffect(() => {
    if (user) {
      checkTourStatus();
    }
  }, [user]);

  // Show welcome tour prompt for first-time users
  useEffect(() => {
    if (user && completedTours.length === 0 && location.pathname.includes('/ats/')) {
      // Check if we should show the prompt
      const hasSeenPrompt = localStorage.getItem(`tour_prompt_shown_${user.id}`);
      if (!hasSeenPrompt && !location.pathname.includes('/login')) {
        setShowWelcomePrompt(true);
        localStorage.setItem(`tour_prompt_shown_${user.id}`, 'true');
      }
    }
  }, [user, completedTours, location]);

  const checkTourStatus = async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/help/tours/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCompletedTours(data.completed_tours || []);
      }
    } catch (error) {
      console.error('Failed to check tour status:', error);
    }
  };

  const markTourComplete = async (tourId) => {
    try {
      const token = localStorage.getItem('ats_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/help/tours/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tour_id: tourId })
      });
      setCompletedTours(prev => [...prev, tourId]);
    } catch (error) {
      console.error('Failed to mark tour complete:', error);
    }
  };

  const startTour = (tourId) => {
    const tour = TOURS[tourId];
    if (!tour) return;

    // Navigate to the starting page if needed
    if (tourId === 'welcome' && !location.pathname.includes('/my-desk')) {
      navigate('/ats/my-desk');
    }

    setCurrentTour(tour);
    setStepIndex(0);
    setRun(true);
    setShowWelcomePrompt(false);
  };

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      if (currentTour && status === STATUS.FINISHED) {
        markTourComplete(currentTour.id);
      }
      setCurrentTour(null);
    }
  };

  // Custom tooltip styles
  const tourStyles = {
    options: {
      primaryColor: '#2563eb',
      textColor: '#1e293b',
      backgroundColor: '#ffffff',
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 10000,
    },
    tooltipContainer: {
      textAlign: 'left',
    },
    buttonNext: {
      backgroundColor: '#2563eb',
      borderRadius: '6px',
      padding: '8px 16px',
    },
    buttonBack: {
      color: '#64748b',
      marginRight: '8px',
    },
    buttonSkip: {
      color: '#94a3b8',
    },
  };

  return (
    <>
      {/* Welcome Tour Prompt Modal */}
      {showWelcomePrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 animate-in fade-in zoom-in duration-300">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">👋</span>
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Welcome to DCS ATS!</h2>
              <p className="text-slate-600 mb-6">
                Would you like a quick tour to learn the basics? It only takes 2 minutes.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowWelcomePrompt(false)}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Skip for now
                </button>
                <button
                  onClick={() => startTour('welcome')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Start Tour
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Joyride Tour */}
      {currentTour && (
        <Joyride
          run={run}
          steps={currentTour.steps}
          stepIndex={stepIndex}
          callback={handleJoyrideCallback}
          continuous
          showProgress
          showSkipButton
          scrollToFirstStep
          spotlightClicks
          styles={tourStyles}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'Finish',
            next: 'Next',
            skip: 'Skip Tour',
          }}
        />
      )}
    </>
  );
};

// Export helper function to start tours programmatically
export const useTours = () => {
  const startTour = (tourId) => {
    // Dispatch custom event to start tour
    window.dispatchEvent(new CustomEvent('start-tour', { detail: { tourId } }));
  };

  const isTourCompleted = (tourId) => {
    // Check from localStorage as a quick check
    const completed = localStorage.getItem(`tour_completed_${tourId}`);
    return !!completed;
  };

  return { startTour, isTourCompleted, availableTours: Object.keys(TOURS) };
};

export default GuidedTours;
